import React, { useState, useEffect, useMemo } from "react";
import Dropdown from "app/components/shared/app/Dropdown";
import MerGraphWrapper from "../MerGraphWrapper";
import { renderTabMessage } from "../../utils/renderTabMessage";
import MerGraphDetails from "../MerGraphDetails";
import { formatMer, tabOptions, TabState } from "../../utils/utils";
import Loader, { LoaderRing } from "app/components/shared/app/Loader";
import Spinner from "app/components/shared/app/Spinner";
import { orZero } from "app/lib/utils/NumUtils";
import { renderError, renderNoDataMessage } from "app/lib/utils/adaya/AdayaUtils";
import { ErrorMessages } from "app/lib/AppErrors";
import { getLogger } from "app/lib/utils/logging";

const { log, warn } = getLogger('MerDesktop', { color: 'red', enabled: false });

// todo: refactor state
const MerDesktop = ({

    activeTab,
    setActiveTab,

    // we pass the states individually to avoid re-rendering of the graph on other data set changes/loading.
    //tabStates,
    last7daysData,
    tabLast7daysData,
    tabLast30daysData,
    tabMonthToDateData,
    tabQuarterToDateData,
    tabYearToDateData,

    merGoal,

    selectedMetric,
    setSelectedMetric,
    metricItems,

    predictionState,

    ...props

}) => {

    const [activeState, setActiveState] = useState(undefined);

    useEffect(() => {
        log(`SELECTED METRIC change`, selectedMetric);
    }, [selectedMetric]);

    useEffect(() => {
        log(`ACTIVE TAB change`, activeTab);
        updateActiveState(activeTab);
    }, [activeTab]);

    useEffect(() => {
        updateActiveState('last7days');
    }, [tabLast7daysData]);

    useEffect(() => {
        updateActiveState('last30days');
    }, [tabLast30daysData]);

    useEffect(() => {
        updateActiveState('monthToDate');
    }, [tabMonthToDateData]);

    useEffect(() => {
        updateActiveState('quarterToDate');
    }, [tabQuarterToDateData]);

    useEffect(() => {
        updateActiveState('yearToDate');
    }, [tabYearToDateData]);

    useEffect(() => {
        log(`MerDesktop selectedMetric changed`, selectedMetric);
        // todo: should only update each local set if it's changed.
    }, [selectedMetric]);

    useEffect(() => {
        log(`MER GOAL change`, merGoal);
    }, [merGoal]);

    useEffect(() => {
        log(`MER GOAL predictionState change`, predictionState);
    }, [predictionState]);

    useEffect(() => {
        log(`MER GOAL metricItems change`, predictionState);
    }, [predictionState]);

    const tabsLoaded = () => {
        return (activeTab != undefined);
    }

    // updates local graph state with a changed tab state if active
    updateActiveState = (changedId) => {
        log(`updateActiveState`, changedId);
        if (changedId && changedId == activeTab) {
            let _activeState;
            switch (activeTab) {
                case 'last7days':
                    _activeState = tabLast7daysData;
                    break;
                case 'last30days':
                    _activeState = tabLast30daysData;
                    break;
                case 'monthToDate':
                    _activeState = tabMonthToDateData;
                    break;
                case 'quarterToDate':
                    _activeState = tabQuarterToDateData;
                    break;
                case 'yearToDate':
                    _activeState = tabYearToDateData;
                    break;
                default:
                    break;
            }
            if (_activeState) {//    && activeTab == changedId) {
                log(`setActiveState`, activeTab, _activeState);
                return setActiveState(_activeState);
            }
        } else {
            // update with empty?
        }
    }

    const getTabState = (tabId) => {
        switch (tabId) {
            case 'last7days':
                return tabLast7daysData;
                break;
            case 'last30days':
                return tabLast30daysData;
                break;
            case 'monthToDate':
                return tabMonthToDateData;
                break;
            case 'quarterToDate':
                return tabQuarterToDateData;
                break;
            case 'yearToDate':
                return tabYearToDateData;
                break;
            default:
                return undefined;
        }
    }

    const currentTabIsLoaded = () => {
        if (!activeState) return false;
        return activeState.state == TabState.Loaded ||
            activeState.state == TabState.Error;
    }

    const currentTabHasData = () => {
        if (!activeState) return false;
        return activeState.data != undefined;
    }

    const currentTabHasError = () => {
        if (!activeState) return false;
        return activeState.state == TabState.Error;
    }

    const renderTabLoader = (label = undefined, size = 'small', color = 'blue') => (
        <div className="tab-loading">
            <div className="loader-wrapper">
                <LoaderRing size={size} color={color} />
                {label && <span className="label">{label}</span>}
            </div>
        </div>
    );

    const renderTabLoaded = (tabState) => {
        return (tabState == undefined) ? <></> : (
            <div className="tab-loaded">
                <div className={`mer-value ${parseFloat(orZero(tabState.data?.metrics?.mer)) > merGoal ? 'status-good' : 'status-bad'}`}>
                    <span>{formatMer(orZero(tabState.data?.metrics?.mer))}</span>
                </div>

                <div className="label">
                    <span>{tabState.label}</span>
                </div>
            </div>
        );
    }

    const renderTabError = (tabState) => !tabState ? <></> : (
        <div className="tab-error">
            <span className="error-icon">⚠️</span>
            <span className="label">{tabState.label}</span>
        </div>
    );

    const renderTab = (tabId) => {
        let tabState = getTabState(tabId);
        if (!tabId || !tabState) return <></>;
        else {
            return <div
                className={`tab ${tabId} ${activeTab == tabId ? 'active' : ''}`}
                key={tabId}
                onClick={() => setActiveTab(tabId)}
            >
                {
                    tabState.state == TabState.Loading ?
                        renderTabLoader(`${tabState.label} loading...`, 'small', 'blue') :

                        tabState.state == TabState.Error ?
                            renderTabError(tabState) :

                            renderTabLoaded(tabState)
                }
            </div>
        }
    }

    // render when data is available/ready
    const renderTabData = (tabState) => !tabState ? <></> : (
        <div className="tab-data chart">
            <Dropdown
                selectedItem={metricItems.find(mi => mi.value === selectedMetric).label}
                setSelection={setSelectedMetric}
                items={metricItems}
            />

            {tabState.data != undefined ?
                <>
                    {tabState.data &&
                        <MerGraphWrapper
                            tabData={tabState.data}
                            selectedMetric={selectedMetric}
                            dailyGoal={merGoal}
                            predictionState={predictionState}
                        />
                    }

                    <MerGraphDetails
                        activeTab={activeTab}
                        activeState={activeState}
                        selectedMetric={selectedMetric}
                    />
                </>

                : renderNoDataMessage()
            }
        </div>
    );

    log('MerDesktop render:', activeTab, activeState);

    return (
        <>
            <div className="mer-page desktop page-layout">

                <div className="cols flex-no-wrap">

                    <div className="col tabs">

                        <div className='tab tab-message'>
                            {tabsLoaded() ?
                                renderTabMessage(activeTab, orZero(activeState?.data?.mer), merGoal) :
                                renderTabLoader(undefined,'small', 'blue')
                            }
                        </div>

                        {tabsLoaded() && Object.keys(tabOptions).map(tabId => {
                            return renderTab(tabId);
                        })}
                    </div>

                    {/* Bar Graph */}
                    <div className="col section">

                        <div className="header">
                            <h2>MER&nbsp;<span>(Media Efficiency Ratio)</span></h2>
                        </div>

                        {!currentTabIsLoaded() ?
                            renderTabLoader(`Loading ${(activeState?.labelLong || '').toLowerCase()} ...`, 'large', 'white') :

                            currentTabHasError() ?
                                renderError(ErrorMessages.defaultErrorMessage(), activeState?.error, "large") :

                                currentTabHasData() ?
                                    renderTabData(activeState) :

                                    renderNoDataMessage()
                        }

                    </div>

                </div>

            </div>
        </>
    )

}

export default MerDesktop;